import React from 'react';
import { useContext, useEffect, useState, useRef } from 'react';
import { Popover, Overlay } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import moment from 'moment';

import DatePickerCustomized from '../date_picker_customized/date_picker_customized.component';
import TxTable from '../tx_table.component';
import { getStockPriceOnDate, parseTransactions } from '../../../../../api';
import AppContext from '../../../../context';

import '../stepone.component.css';
import SearchBar from '../search_bar/search_bar.component';
import { StickyTable, Row, Cell } from 'react-sticky-table';
import NumberFormat from 'react-number-format';
import Loader from '../../../common/loader/loader.component';
import Dropdown from '../../../../common/dropdown/dropdown.component';

const usePrevious = (noOfTxs) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = noOfTxs.length;
  });
  return ref.current;
};

const StepOneManual = React.forwardRef((props, ref) => {
  // Context
  const context = useContext(AppContext);
  // Inline edit

  // Form elements
  const [company_name, set_company_name] = useState('');
  const [comp_id, set_comp_id] = useState();
  const [fincode, set_fincode] = useState(null);
  const [scrip, set_scrip] = useState(null);
  const [type, set_type] = useState('');
  const [shares, set_shares] = useState('');
  const [date_obj, set_date_obj] = useState(null);
  const [date, set_date] = useState('');
  const [price, set_price] = useState('');
  const [cost_value, set_cost_value] = useState(0);
  const [inception_date, set_inception_date] = useState('');
  const [isLastInputisShare, setIsLastInputisShare] = useState(false);
  const dateInput = useRef(null);
  const dateInputContainer = useRef(null);
  const [datePopover, setDatePopover] = useState(false);
  const { searchBarRef, typeSelectRef } = ref;
  //for highlighting last added tx
  const isTxAdded = useRef(false);
  // Form errors
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const lastTxSharesInputRef = useRef(null);
  const lastAddedTxRef = useRef(null);
  const prevTxsLength = usePrevious(props.txs);

  // Search functionality

  const clearComp = () => {
    set_company_name();
    set_comp_id();
    set_inception_date('');
  };

  const onSearchClick = (c) => {
    set_comp_id(c.id);
    set_company_name(c.short_name);
    set_fincode(c.fincode);
    set_scrip(c.scripcode);
    if (moment(c.data_inception_date).isValid()) {
      set_inception_date(c.data_inception_date);
      const inceptionDateObj = new Date(c.data_inception_date);
      if (date_obj && date_obj < inceptionDateObj) {
        set_date_obj(inceptionDateObj);
        set_date(moment(inceptionDateObj).format('DD-MM-YYYY'));
      }
      return;
    }
    set_inception_date('');
  };

  // Form functions
  const positive_num = (v) => typeof v === 'number' && v > 0;
  const date_str = (v) => moment(v, 'D-M-YYYY', true).isValid();

  // Check is a value is valid based on a checker function(if provided)
  const isValid = ([val, checker]) => {
    if (val && val !== undefined && val !== '') {
      if (checker) return checker(val);
      return true;
    }
    return false;
  };

  const clearForm = () => {
    set_comp_id();
    set_fincode(null);
    set_scrip(null);
    set_company_name('');
    set_type('');
    set_shares('');
    set_date('');
    set_date_obj(null);
    set_price('');
    set_cost_value(0);
    set_inception_date('');
    setTimeout(() => {
      dateInput.current && (dateInput.current.value = '');
    }, 0);
  };

  const checkForm = () => {
    const values = [comp_id, company_name, type, shares, date, price, date_obj];
    const validators = [
      null,
      null,
      null,
      positive_num,
      date_str,
      positive_num,
      null,
    ];
    const validations = values.map((v, i) => [v, validators[i]]);
    return validations.every(isValid);
  };

  // Check transaction validity and add if its valid
  const checkAndAdd = () => {
    if (checkForm()) {
      isTxAdded.current = true;
      const tx = {
        company_id: comp_id,
        company_name,
        type,
        shares,
        date,
        date_obj,
        price,
        cost_value: cost_value,
        data_inception_date: inception_date,
        fincode,
        scrip,
      };
      props.setTxs([...props.txs, tx]);
      props.setTxsFincode([...props.txsFincode, fincode]);
      clearForm();
    }
  };

  const get_and_set_price = async (on_date) => {
    if (!fincode || !on_date) return;
    const res = await getStockPriceOnDate(fincode, on_date);
    const stock_price = res.data.data.value;
    check_and_set_price(stock_price);
  };

  const check_and_set_price = (price) => {
    if (price > 0) {
      set_price(price);
    }
  };

  useEffect(() => {
    if (typeof shares === 'number')
      set_cost_value(((price || 0) * (shares || 0)).toFixed(2));
  }, [price, shares]);

  useEffect(() => {
    if (date_obj) {
      get_and_set_price(moment(date_obj).format('DDMMYYYY'));
    }
  }, [fincode, date_obj]);

  const getTypeClass = () => {
    if (type === '') {
      return 'type-select placeholder';
    }
    return 'type-select';
  };

  const handleDateRawChange = (e, minDate) => {
    let dateStr = e.target.value;
    dateInput.current = e.target;
    if (isError) {
      setIsError(false);
    }
    if (moment(dateStr, 'D-M-YYYY', true).isValid()) {
      dateStr = dateStr.split('-').reverse().join('-');
      let date_obj = new Date(dateStr);
      if (date_obj > new Date()) {
        setErrorText('Date cannot be in the future');
        setIsError(true);
        return;
      }
      if (minDate && date_obj < minDate) {
        setErrorText(
          `Date should be later than ${moment(minDate).format('DD-MM-YYYY')}`,
        );
        setIsError(true);
        return;
      }
      set_date_obj(date_obj);
      set_date(moment(date_obj).format('DD-MM-YYYY'));
    }
  };

  useEffect(() => {
    checkAndAdd();
  }, [company_name, type, cost_value, date]);

  useEffect(() => {
    if (company_name && type && price && !shares) {
      setIsLastInputisShare(true);
    }
    if (prevTxsLength !== undefined && props.txs.length !== prevTxsLength) {
      if (lastTxSharesInputRef.current && isLastInputisShare) {
        lastTxSharesInputRef.current.click();
        setIsLastInputisShare(false);
        return;
      }
      props.containerRef.current.parentElement.click();
    }
  });

  useEffect(() => {
    if (props.txs.length && isTxAdded.current) {
      lastAddedTxRef.current.scrollIntoView();
      lastAddedTxRef.current.classList.add('highlight');
      setTimeout(() => {
        if (lastAddedTxRef.current) {
          lastAddedTxRef.current.classList.remove('highlight');
        }
      }, 1800);
      isTxAdded.current = false;
    }
  }, [props.txs]);

  const popover = (
    <Popover id="date-popover">
      <Popover.Content className="">
        Please add a company to proceed
      </Popover.Content>
    </Popover>
  );

  return (
    <>
      <section className="position-relative">
        <div className="step-info mb-2">
          Please enter unadjusted amount, we will adjust for bonus and splits in
          the next step
        </div>
        {props.txs.length > 0 && props.invalidComps.length > 0 && (
          <span className="text-danger sell-tx-error manual">
            {props.invalidComps.length === 1
              ? 'Invalid sell transaction: You do not have enough shares.'
              : 'Invalid sell transactions: You do not have enough shares.'}
          </span>
        )}
        {isError && errorText && (
          <span className="text-danger sell-tx-error manual">{errorText}</span>
        )}
      </section>
      <div className="position-relative overflow-auto flex-grow">
        {!context.is_sample && (
          <Overlay
            show={datePopover}
            target={dateInputContainer.current}
            placement="bottom"
            rootClose={true}
            onHide={() => setDatePopover(false)}
          >
            {popover}
          </Overlay>
        )}
        {props.isLoaderVisible ? (
          <div className="overlay-loader-cont">
            <Loader />
          </div>
        ) : null}
        <StickyTable
          className="add-tx-table table-responsive"
          stickyFooterCount={1}
          leftStickyColumnCount={0}
        >
          <Row className="sticky-table-thead">
            <Cell className="pl-3 pr-3 search-width">COMPANY NAME</Cell>
            <Cell className="pl-3 pr-3">TRANSACTION TYPE</Cell>
            <Cell className="text-right pl-3 pr-5">SHARES</Cell>
            <Cell className="pl-3 pr-3">DATE</Cell>
            <Cell className="text-right pl-3 pr-3">SHARE PRICE</Cell>
            <Cell className="text-right pl-3 pr-3">AMOUNT</Cell>
            <Cell className="text-right pl-3 pr-3">{'   '}</Cell>
          </Row>
          <TxTable
            setTxs={props.setTxs}
            txs={props.txs}
            txsFincode={props.txsFincode}
            setTxsFincode={props.setTxsFincode}
            removeTx={props.removeTx}
            ref={{ lastTxSharesInputRef, lastAddedTxRef }}
            containerRef={props.containerRef}
            invalidComps={props.invalidComps}
            isError={isError}
            setIsError={setIsError}
            errorText={errorText}
            setErrorText={setErrorText}
          ></TxTable>
          <Row className="table-row input-row">
            <Cell className="pl-3">
              {(!comp_id && (
                <SearchBar
                  placeholder="+ Add a company"
                  onSearchClick={onSearchClick}
                  suggestionsPosition={props.searchDropdownPos}
                  padding={true}
                  containerRef={props.containerRef}
                  inputClass="input-br bare"
                  ref={searchBarRef}
                />
              )) || (
                <div className="comp-name" onClick={(e) => clearComp()}>
                  {company_name}
                  <span>
                    &nbsp;
                    <X color="#BFBFBF" />
                  </span>
                </div>
              )}
            </Cell>
            <Cell className="pl-3">
              <Dropdown
                selected={type}
                setSelected={set_type}
                placeholder="Buy/Sell"
                options={['BUY', 'SELL']}
                containerClass="input-br select"
                ref={typeSelectRef}
                optionsPosition={props.typeDropdownPos}
              />
            </Cell>
            <Cell className="text-right pr-5">
              <NumberFormat
                value={shares}
                className="text-right input-br bare"
                allowNegative={false}
                onValueChange={(values) => {
                  const { value } = values;
                  set_shares(parseInt(value));
                }}
                placeholder="Shares"
                allowLeadingZeros={false}
              />
            </Cell>
            <Cell className="pl-3">
              <div
                ref={dateInputContainer}
                onClick={() => !comp_id && setDatePopover(true)}
              >
                <DatePickerCustomized
                  selected={date_obj}
                  onSelect={(d) => {
                    set_date_obj(d);
                    set_date(moment(d).format('DD-MM-YYYY'));
                  }}
                  onChangeRaw={handleDateRawChange}
                  onBlur={() => !date && setIsError(false)}
                  type={type}
                  minDate={inception_date}
                  isDisabled={!comp_id}
                  inputClass="input-br bare"
                />
              </div>
            </Cell>
            <Cell className="text-right pr-3">
              <NumberFormat
                value={price}
                className="text-right input-br bare"
                allowNegative={false}
                onValueChange={(values) => {
                  const { value } = values;
                  check_and_set_price(parseFloat(value));
                }}
                placeholder="Share price"
                allowLeadingZeros={false}
              />
            </Cell>
            <Cell className="text-right pr-3">{cost_value}</Cell>
            <Cell> </Cell>
          </Row>
        </StickyTable>
      </div>
    </>
  );
});

export default StepOneManual;
